import { filter } from "lodash";
import { useState, useEffect } from "react";
// material
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// components
import Page from "../../components/Page";
import Scrollbar from "../../components/Scrollbar";
import SearchNotFound from "../../components/SearchNotFound";
import {
  TableTitle,
  TableHeader,
} from "../../components/_dashboard/user";
//
import { useTranslation } from "react-i18next";
import { db } from "../../database/db";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.username.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function AcceptMoneyRequest() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("username");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [requestList, setRequestList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: "sno", label: "#", alignRight: false },
    { id: "username", label: t("User_Name"), alignRight: false },
    { id: "email", label: t("Email"), alignRight: false },
    { id: "paypal", label: "PayPal Email", alignRight: false },
    { id: "amount", label: t("Amount"), alignRight: false },
    { id: "transactionid", label: t("Transaction_ID"), alignRight: false },
    { id: "action", label: t("Action"), alignRight: false },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "PayoutRequests"), where("status", "==", "completed"));
      const querySnapshot = await getDocs(q);
      const list = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        const userDoc = await getDoc(doc(db, "User", data.userId));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        list.push({
          id: docSnapshot.id,
          username: userData.name || "Unknown",
          email: userData.email || "N/A",
          ...data
        });
      }
      setRequestList(list);
    } catch (error) {
      console.error("Error fetching accepted requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = requestList.map((n) => n.username);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requestList.length) : 0;

  const filteredRequests = applySortFilter(
    requestList,
    getComparator(order, orderBy)
  );

  const isNotFound = filteredRequests.length === 0 && !loading;

  const TableCellStyles = styled((props) => <TableCell {...props} />)(
    ({ theme }) => ({
      border: "1px solid #e3e6f0",
      fontSize: "12px",
    })
  );

  return (
    <Page title="Money Request | Accept Request">
      <Container>
        <Card sx={{ borderRadius: "5px" }}>
          <TableTitle title={t("Accept_Request")} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, p: "18px" }}>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={requestList.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredRequests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const {
                        id,
                        username,
                        email,
                        paypalEmail,
                        netAmount,
                      } = row;
                      const isItemSelected = selected.indexOf(username) !== -1;

                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCellStyles align="left">
                            {index + 1}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {username}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {email}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {paypalEmail}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            ${netAmount?.toFixed(2)}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {id}
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            <Button
                              variant="contained"
                              sx={{
                                borderRadius: "6px",
                                boxShadow: "none",
                                backgroundColor: "#1cc88a",
                              }}
                            >
                              {t("Paid")}
                            </Button>
                          </TableCellStyles>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCellStyles colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCellStyles
                        align="center"
                        colSpan={7}
                        sx={{ py: 3 }}
                      >
                        <SearchNotFound />
                      </TableCellStyles>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
